import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faArrowRight, faDiamond, faLocationDot, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import heroImage from './NimaiGarg.png';
import vtLogo from './VTLogo.png';
import logoImage from './logo.png';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  :root {
    --ink: #061014;
    --ink-soft: rgba(6, 16, 20, 0.68);
    --glass: rgba(255, 255, 255, 0.58);
    --glass-strong: rgba(255, 255, 255, 0.76);
    --line: rgba(255, 255, 255, 0.64);
    --maroon: #861f41;
    --orange: #e5751f;
    --cyan: #0f8ea3;
    --green: #28745a;
    --night: #071115;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    scroll-behavior: smooth;
  }

  html {
    background: #edf3f2;
  }

  body {
    min-width: 320px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: var(--ink);
    background:
      radial-gradient(circle at 14% 8%, rgba(229, 117, 31, 0.11), transparent 24%),
      radial-gradient(circle at 78% 6%, rgba(15, 142, 163, 0.16), transparent 30%),
      radial-gradient(circle at 54% 88%, rgba(15, 142, 163, 0.08), transparent 38%),
      linear-gradient(145deg, #fcfdfb 0%, #eff8f8 48%, #fffdf8 100%);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    color: inherit;
  }

  ::selection {
    color: #ffffff;
    background: rgba(134, 31, 65, 0.72);
  }

`;

const Container = styled.div`
  min-height: 100vh;
  isolation: isolate;
  position: relative;
  overflow: hidden;
`;

const Aurora = styled.div`
  position: fixed;
  inset: -18%;
  z-index: -3;
  pointer-events: none;
  background:
    radial-gradient(circle at 18% 18%, rgba(229, 117, 31, 0.16), transparent 25%),
    radial-gradient(circle at 72% 14%, rgba(15, 142, 163, 0.22), transparent 28%),
    radial-gradient(circle at 80% 76%, rgba(255, 244, 220, 0.28), transparent 34%),
    conic-gradient(from 160deg at 52% 44%, rgba(255, 255, 255, 0.94), rgba(244, 236, 221, 0.2), rgba(15, 142, 163, 0.14), rgba(229, 117, 31, 0.06), rgba(255, 255, 255, 0.96));
  filter: blur(5px) saturate(118%);
`;

const LensField = styled.div`
  position: fixed;
  inset: 0;
  z-index: -2;
  pointer-events: none;
  background:
    linear-gradient(120deg, rgba(255,255,255,0.32) 0 1px, transparent 1px 92px),
    linear-gradient(30deg, rgba(255,255,255,0.22) 0 1px, transparent 1px 108px);
  mask-image: radial-gradient(circle at 50% 22%, black, transparent 78%);
  opacity: 0.64;
`;

const Shell = styled.div`
  position: relative;
  z-index: 1;
`;

const Header = styled.header`
  width: min(1180px, calc(100% - 36px));
  margin: 18px auto 0;
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  position: sticky;
  top: 14px;
  z-index: 20;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.42)),
    radial-gradient(circle at 8% 0%, rgba(255, 255, 255, 0.86), transparent 36%);
  border: 1px solid rgba(255, 255, 255, 0.74);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.82),
    0 20px 70px rgba(6, 16, 20, 0.12);

  @media (max-width: 640px) {
    width: calc(100% - 20px);
    top: 10px;
  }
`;

const Logo = styled.img`
  width: clamp(112px, 12vw, 142px);
  height: auto;
  display: block;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px;
  border-radius: 999px;
  background: rgba(6, 16, 20, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.56);

  a {
    text-decoration: none;
    font-size: 0.88rem;
    font-weight: 800;
    color: rgba(6, 16, 20, 0.64);
    padding: 10px 14px;
    border-radius: 999px;
    transition: color 0.25s ease, background 0.25s ease, transform 0.25s ease;
  }

  a:hover {
    color: var(--ink);
    background: rgba(255, 255, 255, 0.78);
    transform: translateY(-1px);
  }

  @media (max-width: 780px) {
    display: none;
  }
`;

const HeaderSocials = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.08rem;

  a {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    color: var(--ink);
    background: rgba(255, 255, 255, 0.54);
    border: 1px solid rgba(255, 255, 255, 0.72);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
    transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease, border-radius 0.25s ease;
  }

  a:hover {
    transform: translateY(-2px) scale(1.03);
    border-radius: 20px;
    background: var(--night);
    color: #ffffff;
  }
`;

const HeroSection = styled.section`
  width: min(1180px, calc(100% - 40px));
  min-height: calc(100vh - 110px);
  margin: 0 auto;
  padding: 64px 0 56px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.92fr);
  align-items: center;
  gap: 62px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding-top: 72px;
  }

  @media (max-width: 560px) {
    width: calc(100% - 28px);
  }
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  max-width: 100%;
  padding: 10px 14px;
  margin-bottom: 22px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.76);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 10px 32px rgba(6, 16, 20, 0.08);
  color: #5b1630;
  font-size: 0.9rem;
  font-weight: 900;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--orange), var(--maroon));
  box-shadow: 0 0 0 7px rgba(229, 117, 31, 0.18);
`;

const HeroTitle = styled(motion.h1)`
  max-width: 720px;
  font-size: clamp(3rem, 5.8vw, 5.85rem);
  line-height: 0.98;
  font-weight: 900;
  letter-spacing: 0;
  color: var(--ink);
  margin-bottom: 22px;
`;

const Accent = styled.span`
  color: transparent;
  background: linear-gradient(100deg, var(--maroon), var(--orange) 52%, var(--cyan));
  -webkit-background-clip: text;
  background-clip: text;
  font-style: italic;
`;

const HeroSubtitle = styled(motion.p)`
  max-width: 650px;
  font-size: clamp(1.02rem, 1.35vw, 1.2rem);
  line-height: 1.68;
  color: var(--ink-soft);
  margin-bottom: 28px;
  font-weight: 450;
`;

const HeroActions = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 16px 22px 16px 24px;
  display: inline-flex;
  align-items: center;
  gap: 11px;
  background: linear-gradient(135deg, #061014, #17262a);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 900;
  box-shadow: 0 18px 44px rgba(6, 16, 20, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-radius 0.25s ease;

  &:hover {
    transform: translateY(-3px) scale(1.01);
    border-radius: 22px;
    box-shadow: 0 26px 58px rgba(6, 16, 20, 0.26), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
`;

const SecondaryButton = styled.a`
  border-radius: 999px;
  padding: 15px 22px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.78);
  color: var(--ink);
  font-size: 1rem;
  font-weight: 900;
  text-decoration: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 16px 40px rgba(6, 16, 20, 0.08);
  backdrop-filter: blur(18px) saturate(170%);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  transition: transform 0.25s ease, border-radius 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateY(-3px) scale(1.01);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.78);
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  min-height: 540px;

  @media (max-width: 960px) {
    min-height: 520px;
  }

  @media (max-width: 560px) {
    min-height: 460px;
  }
`;

const PortraitCard = styled.div`
  position: absolute;
  inset: 0 0 34px auto;
  width: min(420px, 100%);
  border-radius: 44px;
  padding: 14px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.32)),
    radial-gradient(circle at 24% 0%, rgba(255, 255, 255, 0.92), transparent 35%);
  border: 1px solid rgba(255, 255, 255, 0.76);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.88),
    0 34px 100px rgba(6, 16, 20, 0.18);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background:
      radial-gradient(circle at 18% 10%, rgba(255, 255, 255, 0.62), transparent 18%),
      linear-gradient(115deg, rgba(255,255,255,0.28), transparent 46%);
    mix-blend-mode: screen;
  }

  @media (max-width: 960px) {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Portrait = styled.img`
  width: 100%;
  height: 470px;
  object-fit: cover;
  object-position: 55% center;
  border-radius: 34px;
  display: block;

  @media (max-width: 560px) {
    height: 420px;
  }
`;

const FloatingBadge = styled(motion.div)`
  position: absolute;
  left: -18px;
  bottom: 26px;
  width: min(310px, 86%);
  padding: 18px;
  border-radius: 28px;
  background: rgba(6, 16, 20, 0.76);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 24px 70px rgba(6, 16, 20, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.84rem;
    font-weight: 800;
    margin-bottom: 8px;
  }

  strong {
    display: block;
    font-size: 1.2rem;
    line-height: 1.25;
  }
`;

const StatStrip = styled(motion.div)`
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto 44px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    width: calc(100% - 28px);
  }
`;

const StatCard = styled.div`
  min-height: 128px;
  padding: 22px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.56);
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.76), 0 18px 54px rgba(6, 16, 20, 0.08);
  backdrop-filter: blur(22px) saturate(168%);
  -webkit-backdrop-filter: blur(22px) saturate(168%);

  strong {
    display: block;
    font-size: 1.64rem;
    color: var(--maroon);
    margin-bottom: 7px;
  }

  span {
    color: rgba(6, 16, 20, 0.62);
    font-weight: 500;
    font-size: 0.92rem;
    line-height: 1.45;
  }
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 68px 0;

  @media (max-width: 560px) {
    width: calc(100% - 28px);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 26px;
  margin-bottom: 32px;

  @media (max-width: 760px) {
    align-items: start;
    flex-direction: column;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.1rem, 4.6vw, 3.7rem);
  line-height: 1;
  letter-spacing: 0;
  font-weight: 900;
  color: var(--ink);
`;

const SectionKicker = styled.p`
  max-width: 510px;
  color: rgba(6, 16, 20, 0.64);
  line-height: 1.7;
  font-weight: 450;
`;

const GlassPanel = styled(motion.div)`
  position: relative;
  border-radius: 38px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0.36)),
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.82), transparent 36%);
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.86),
    0 28px 90px rgba(6, 16, 20, 0.1);
  backdrop-filter: blur(30px) saturate(175%);
  -webkit-backdrop-filter: blur(30px) saturate(175%);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    pointer-events: none;
    background:
      radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.64), transparent 24%),
      radial-gradient(circle at 86% 100%, rgba(15, 142, 163, 0.12), transparent 30%);
    mix-blend-mode: screen;
  }
`;

const AboutPanel = styled(GlassPanel)`
  display: grid;
  grid-template-columns: 0.86fr 1.14fr;
  gap: 22px;
  padding: 18px;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const VTCard = styled.div`
  position: relative;
  min-height: 330px;
  padding: 30px;
  border-radius: 30px;
  color: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background:
    radial-gradient(circle at 88% 14%, rgba(229, 117, 31, 0.82), transparent 24%),
    linear-gradient(145deg, #861f41 0%, #4b1027 58%, #071115 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 20px 60px rgba(134, 31, 65, 0.18);

  &::after {
    content: '';
    position: absolute;
    right: -58px;
    bottom: -64px;
    width: 260px;
    height: 260px;
    background-image: url(${vtLogo});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    opacity: 0.14;
  }

  span {
    position: relative;
    z-index: 1;
    color: rgba(255, 255, 255, 0.72);
    font-weight: 900;
    text-transform: uppercase;
    font-size: 0.78rem;
  }

  h3 {
    position: relative;
    z-index: 1;
    font-size: clamp(2rem, 4vw, 2.85rem);
    line-height: 1.04;
    margin-top: 15px;
  }

  p {
    position: relative;
    z-index: 1;
    color: rgba(255, 255, 255, 0.76);
    line-height: 1.68;
    font-weight: 450;
  }
`;

const AboutCopy = styled.div`
  position: relative;
  z-index: 1;
  padding: 24px 24px 20px;

  p {
    color: rgba(6, 16, 20, 0.7);
    font-size: 1.07rem;
    line-height: 1.85;
    margin-bottom: 18px;
    font-weight: 450;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
`;

const Tag = styled.span`
  padding: 10px 13px;
  border-radius: 999px;
  background: rgba(134, 31, 65, 0.08);
  color: #641833;
  border: 1px solid rgba(134, 31, 65, 0.1);
  font-size: 0.88rem;
  font-weight: 900;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.a)`
  position: relative;
  min-height: 310px;
  padding: 28px;
  border-radius: 34px;
  text-decoration: none;
  color: var(--ink);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.34)),
    radial-gradient(circle at 16% 0%, rgba(255, 255, 255, 0.76), transparent 32%);
  border: 1px solid rgba(255, 255, 255, 0.76);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 18px 58px rgba(6, 16, 20, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  backdrop-filter: blur(26px) saturate(172%);
  -webkit-backdrop-filter: blur(26px) saturate(172%);
  transition: transform 0.28s ease, box-shadow 0.28s ease, border-radius 0.28s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 82% 16%, ${({ $tone }) => $tone}, transparent 32%);
    opacity: 0.5;
    transition: opacity 0.28s ease, transform 0.28s ease;
  }

  &:hover {
    transform: translateY(-7px) scale(1.01);
    border-radius: 40px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86), 0 30px 88px rgba(6, 16, 20, 0.14);
  }

  &:hover::before {
    opacity: 0.72;
    transform: scale(1.12);
  }
`;

const ProjectContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ProjectType = styled.span`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border-radius: 999px;
  background: rgba(6, 16, 20, 0.06);
  color: rgba(6, 16, 20, 0.62);
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
`;

const ProjectName = styled.h3`
  font-size: clamp(1.6rem, 3vw, 2.25rem);
  line-height: 1.08;
  margin: 26px 0 13px;
`;

const ProjectDesc = styled.p`
  color: rgba(6, 16, 20, 0.66);
  line-height: 1.68;
  font-weight: 450;
`;

const ProjectMeta = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: fit-content;
  margin-top: 28px;
  color: #5b1630;
  font-weight: 900;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SkillCard = styled(motion.div)`
  min-height: 216px;
  padding: 24px;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.54);
  border: 1px solid rgba(255, 255, 255, 0.76);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.74), 0 18px 54px rgba(6, 16, 20, 0.08);
  backdrop-filter: blur(22px) saturate(168%);
  -webkit-backdrop-filter: blur(22px) saturate(168%);

  h3 {
    font-size: 1.12rem;
    margin-bottom: 12px;
  }

  p {
    color: rgba(6, 16, 20, 0.64);
    line-height: 1.64;
    font-weight: 450;
  }
`;

const ContactSection = styled.section`
  width: min(1180px, calc(100% - 40px));
  margin: 18px auto 38px;
  padding: 20px;
  border-radius: 40px;
  background:
    linear-gradient(135deg, rgba(0, 0, 0, 0.98), rgba(5, 7, 8, 0.96)),
    radial-gradient(circle at 8% 4%, rgba(255, 255, 255, 0.12), transparent 34%),
    radial-gradient(circle at 88% 86%, rgba(15, 142, 163, 0.16), transparent 34%);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 34px 100px rgba(6, 16, 20, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.16);

  @media (max-width: 560px) {
    width: calc(100% - 28px);
  }
`;

const ContactInner = styled.div`
  padding: 40px;
  border-radius: 30px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 30px;
  align-items: center;
  background: rgba(0, 0, 0, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(26px) saturate(150%);
  -webkit-backdrop-filter: blur(26px) saturate(150%);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: 26px;
  }
`;

const ContactCopy = styled.div`
  h2 {
    font-size: clamp(2rem, 4.4vw, 3.7rem);
    line-height: 1;
    margin-bottom: 14px;
  }

  p {
    max-width: 650px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.7;
    font-weight: 450;
  }
`;

const ContactActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 760px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  min-width: 174px;
  padding: 15px 20px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--ink);
  font-weight: 900;
  text-decoration: none;
  transition: transform 0.25s ease, border-radius 0.25s ease, opacity 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    border-radius: 22px;
    opacity: 0.94;
  }
`;

const ContactSocials = styled.div`
  display: flex;
  gap: 10px;
  font-size: 1.18rem;

  a {
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.16);
    transition: transform 0.25s ease, background 0.25s ease, border-radius 0.25s ease;
  }

  a:hover {
    transform: translateY(-3px);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Footer = styled.footer`
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 28px 0 46px;
  color: rgba(6, 16, 20, 0.52);
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 560px) {
    width: calc(100% - 28px);
  }
`;

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.65, ease: 'easeOut' },
};

const Home = () => {
  const projects = [
    {
      name: 'Bright Sparks Academy',
      type: 'Education Nonprofit',
      desc: 'A 501(c)(3) nonprofit delivering free personalized STEM tutoring across countries through trained volunteer educators.',
      link: 'https://brightsparks.academy',
      tone: 'rgba(134, 31, 65, 0.12)',
    },
    {
      name: 'Aircraft Adventure',
      type: 'Aerospace + Hardware',
      desc: 'A browser-based flight simulator with realistic control dynamics, Arduino cockpit controls, and an artificial intelligence co-pilot for exploring aerospace concepts.',
      link: 'https://aircraftadventure.flightqueststudios.com/',
      tone: 'rgba(15, 142, 163, 0.24)',
    },
    {
      name: 'NetReach',
      type: 'Artificial Intelligence + Access',
      desc: 'A web diagnostic platform that measures latency, jitter, bandwidth, and packet loss while explaining internet issues in plain language.',
      link: 'https://netreach.flightqueststudios.com/',
      tone: 'rgba(40, 116, 90, 0.24)',
    },
    {
      name: 'GourmetChef',
      type: 'Artificial Intelligence Food Tech',
      desc: 'A recipe personalization app that adapts meals around ingredients, dietary needs, calories, and user preferences.',
      link: 'https://gourmetchef.app',
      tone: 'rgba(229, 117, 31, 0.24)',
    },
  ];

  const skills = [
    { title: 'Software Engineering', desc: 'React, Next.js, JavaScript, Node.js, Firebase, Firestore, Python, GitHub' },
    { title: 'Artificial Intelligence Products', desc: 'LLM assistants, personalization systems, data modeling, prompt design, API integration' },
    { title: 'Design + Leadership', desc: 'Figma, product strategy, nonprofit operations, team systems, visual direction' },
  ];

  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <Aurora />
        <LensField />
        <Shell>
          <Header>
            <a href="#top" aria-label="Go to top"><Logo src={logoImage} alt="Nimai Garg logo" /></a>
            <Nav>
              <a href="#about">About</a>
              <a href="#projects">Selected Work</a>
              <a href="#skills">Capabilities</a>
              <a href="#contact">Contact</a>
            </Nav>
            <HeaderSocials>
              <a href="https://linkedin.com/in/nimaigarg" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FontAwesomeIcon icon={faLinkedin} /></a>
              <a href="https://github.com/CalifornianSushi" target="_blank" rel="noreferrer" aria-label="GitHub"><FontAwesomeIcon icon={faGithub} /></a>
            </HeaderSocials>
          </Header>

          <HeroSection id="top">
            <div>
              <Eyebrow initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <Dot /> Freshman at Virginia Tech
              </Eyebrow>
              <HeroTitle initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.05 }}>
                Building at the edge of <Accent>aerospace and AI</Accent>.
              </HeroTitle>
              <HeroSubtitle initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.15 }}>
                I’m Nimai Garg, a Virginia Tech freshman from Cupertino focused on aerospace engineering, computer science, and artificial intelligence. I like building flight sims, useful tools, and projects that make technical ideas feel hands-on.
              </HeroSubtitle>
              <HeroActions initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.25 }}>
                <PrimaryButton onClick={scrollToProjects}>Explore Projects <FontAwesomeIcon icon={faArrowRight} /></PrimaryButton>
                <SecondaryButton href="mailto:nimaigarg@brightsparks.academy">Contact Me <FontAwesomeIcon icon={faWandMagicSparkles} /></SecondaryButton>
              </HeroActions>
            </div>

            <HeroVisual initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <PortraitCard>
                <Portrait src={heroImage} alt="Nimai Garg" />
              </PortraitCard>
              <FloatingBadge initial={{ opacity: 0, x: -22 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.55 }}>
                <span><FontAwesomeIcon icon={faLocationDot} /> Blacksburg + Cupertino</span>
                <strong>Aerospace Engineering, Computer Science, and Artificial Intelligence</strong>
              </FloatingBadge>
            </HeroVisual>
          </HeroSection>

          <StatStrip initial="initial" whileInView="whileInView" viewport={{ once: true }} transition={{ staggerChildren: 0.08 }}>
            <StatCard><strong>50+</strong><span>Countries visited</span></StatCard>
            <StatCard><strong>7</strong><span>Continents</span></StatCard>
            <StatCard><strong>150+</strong><span>Flights taken</span></StatCard>
            <StatCard><strong>VT</strong><span>Aerospace Engineering, Computer Science, and Artificial Intelligence</span></StatCard>
          </StatStrip>

          <Section id="about">
            <SectionHeader>
              <SectionTitle>About</SectionTitle>
              <SectionKicker>I care about building technical work that feels sharp, useful, and actually finished, especially across aerospace, computing, and artificial intelligence.</SectionKicker>
            </SectionHeader>
            <AboutPanel {...fadeUp}>
              <VTCard>
                <div>
                  <span>Current Chapter</span>
                  <h3>Virginia Tech</h3>
                </div>
                <p>Freshman focused on Aerospace Engineering, Computer Science, and Artificial Intelligence, with an eye toward systems that connect software, hardware, and flight.</p>
              </VTCard>
              <AboutCopy>
                <p>
                  I’m a freshman at Virginia Tech and the founder of Bright Sparks Academy, a nonprofit focused on free, personalized STEM education. My work blends engineering, software, and teaching to turn difficult ideas into hands-on experiences.
                </p>
                <p>
                  Across projects like Aircraft Adventure, NetReach, GourmetChef, and Bright Sparks Academy, I build full-stack products, hardware-connected simulations, and artificial intelligence tools designed around real users and real problems.
                </p>
                <Tags>
                  <Tag>Aerospace Engineering</Tag>
                  <Tag>Computer Science</Tag>
                  <Tag>Artificial Intelligence</Tag>
                  <Tag>Hardware Prototyping</Tag>
                  <Tag>Flight Simulation</Tag>
                  <Tag>Founder</Tag>
                </Tags>
              </AboutCopy>
            </AboutPanel>
          </Section>

          <Section id="projects">
            <SectionHeader>
              <SectionTitle>Selected Work</SectionTitle>
              <SectionKicker>A focused set of aerospace, software, nonprofit, and artificial intelligence projects with real users and clear purpose.</SectionKicker>
            </SectionHeader>
            <ProjectsGrid>
              {projects.map((project, index) => (
                <ProjectCard
                  key={project.name}
                  href={project.link}
                  target="_blank"
                  rel="noreferrer"
                  $tone={project.tone}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: index * 0.06 }}
                  viewport={{ once: true, amount: 0.18 }}
                >
                  <ProjectContent>
                    <ProjectType><FontAwesomeIcon icon={faDiamond} /> {project.type}</ProjectType>
                    <ProjectName>{project.name}</ProjectName>
                    <ProjectDesc>{project.desc}</ProjectDesc>
                  </ProjectContent>
                  <ProjectMeta>Open project <FontAwesomeIcon icon={faArrowRight} /></ProjectMeta>
                </ProjectCard>
              ))}
            </ProjectsGrid>
          </Section>

          <Section id="skills">
            <SectionHeader>
              <SectionTitle>Capabilities</SectionTitle>
              <SectionKicker>Technical range across product development, artificial intelligence, aerospace systems, hardware prototyping, and mission-driven leadership.</SectionKicker>
            </SectionHeader>
            <SkillsGrid>
              {skills.map((skill, index) => (
                <SkillCard
                  key={skill.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: index * 0.08 }}
                  viewport={{ once: true, amount: 0.2 }}
                >
                  <h3>{skill.title}</h3>
                  <p>{skill.desc}</p>
                </SkillCard>
              ))}
            </SkillsGrid>
          </Section>

          <ContactSection id="contact">
            <ContactInner>
              <ContactCopy>
                <h2>Let’s build something ambitious.</h2>
                <p>I’m open to engineering projects, research conversations, startup ideas, nonprofit partnerships, and opportunities across aerospace, computer science, and artificial intelligence.</p>
              </ContactCopy>
              <ContactActions>
                <ContactButton href="mailto:nimaigarg@brightsparks.academy">Email Me <FontAwesomeIcon icon={faArrowRight} /></ContactButton>
                <ContactSocials>
                  <a href="https://linkedin.com/in/nimaigarg" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FontAwesomeIcon icon={faLinkedin} /></a>
                  <a href="https://github.com/CalifornianSushi" target="_blank" rel="noreferrer" aria-label="GitHub"><FontAwesomeIcon icon={faGithub} /></a>
                </ContactSocials>
              </ContactActions>
            </ContactInner>
          </ContactSection>

          <Footer>
            <span>© 2026 Nimai Garg. All rights reserved.</span>
            <span>Cupertino · Blacksburg · Aerospace Engineering · Computer Science · Artificial Intelligence</span>
          </Footer>
        </Shell>
      </Container>
    </>
  );
};

export default Home;
